import { useEffect, useState } from "react";
import * as uuid from "uuid";
import { usePermissions } from "@/hooks/usePermissions";
import LoggingAPI from "@/api/logging";
import { Button, Modal, TextInput } from "@monolith-forensics/monolith-ui";
import ButtonMenu from "@/components/Forms/components/ButtonMenu";
import { useCreateCaseFile } from "../files.query";
import { File } from "../files.types";

export const CreateCaseFolderModal: React.FC<{
  show: boolean;
  onClose?: () => void;
  onSubmit?: (data: File) => void;
  onCancel?: () => void;
  defaultFormData: {
    name?: string;
    parent_uuid: string;
    case_uuid: string;
    case_id: number;
  };
  buttonProps?: {
    submitText?: string;
    cancelText?: string;
  };
}> = ({
  show,
  onClose,
  onSubmit,
  onCancel,
  buttonProps = {},
  defaultFormData,
}) => {
  const [submitting, setSubmitting] = useState(false);
  const [name, setName] = useState(defaultFormData.name);
  const [error, setError] = useState<string | null>(null);
  const { currentUser } = usePermissions();
  const mutation = useCreateCaseFile();
  const { mutate: createCaseFile } = mutation;

  const resetForm = () => {
    setName(defaultFormData.name || "");
    setError(null);
  };

  const handleSubmit = async () => {
    if (!name) {
      setError("Folder name is required");
      return;
    }

    setError(null);

    const _uuid = uuid.v7();
    setSubmitting(true);
    createCaseFile(
      {
        ...defaultFormData,
        name: name,
        uuid: _uuid,
        is_folder: true,
      },
      {
        onSuccess: (data) => {
          onSubmit?.(data.data);
          setSubmitting(false);
          resetForm();
        },
        onError: (error) => {
          setSubmitting(false);
          console.error(error);
        },
      }
    );
  };

  const handleCancel = () => {
    resetForm();
    onCancel?.();
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        handleCancel();
      }

      if (e.key === "Enter") {
        handleSubmit();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [show, name]);

  return (
    <Modal open={show} onClose={onClose}>
      <Modal.Title>Create Folder</Modal.Title>
      <div style={{ fontWeight: 600, margin: "20px 0px", width: "350px" }}>
        <TextInput
          value={name}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            setName(e.target.value);
          }}
          label="Folder Name"
          required={true}
          description="The name of the folder to create."
          placeholder="Enter a folder name"
          error={error || undefined}
        />
      </div>
      <ButtonMenu>
        <Button
          size="xs"
          variant="subtle"
          onClick={handleCancel}
          disabled={submitting}
        >
          {buttonProps?.cancelText || "Cancel"}
        </Button>
        <Button
          size="xs"
          color="primary"
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting}
          loading={submitting}
        >
          {buttonProps?.submitText || "Create Folder"}
        </Button>
      </ButtonMenu>
    </Modal>
  );
};
